import React, { useContext, useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';

import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import StarsInfo from '@swf/components/StarsInfo/StarsInfo';
import Translation from '@swf/components/Translation/Translation';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { Speed } from '@swf/poker';

import Container from '../../Container/Container';
import CreateTournamentContext from '../../CreateTournamentContext/CreateTournamentContext';
import useSaveFormFields from '../../hooks/useSaveFormFields';
import type { TournamentWizardContext } from '../../utils/common';
import { ModalPages } from '../../utils/common';
import getStandaloneOptions from '../../utils/getStandaloneOptions';

import {
  TXTCLI_Breaks,
  TXTCLI_Format,
  TXTCLI_Late_Registration,
  TXTCLI_Payout,
  TXTCLI_Re_Entry,
  TXTCLI_Speed,
  TXTCLI_mins
} from './MoreDetailsForm.messages.json';
import styles from './MoreDetailsForm.scss';

const MoreDetailsForm: React.FC = () => {
  const { setState } = useContext(CreateTournamentContext);
  const form = useForm();
  const {
    data: { tournamentStructuresResponse, loading, createClubTournamentResponse }
  } = useWizardContext<TournamentWizardContext>();

  const {
    tournLevelLengthsOptions,
    tournFormatsOptions,
    tournReentryOptions,
    tournLateRegLevelsOptions,
    tournSyncBreaksOptions,
    payoutsOptions
  } = useMemo(
    () => getStandaloneOptions(tournamentStructuresResponse.data),
    [tournamentStructuresResponse.data]
  );

  const {
    input: { value: levelLength }
  } = useField('levelLength', {
    subscription: { value: true },
    initialValue: tournLevelLengthsOptions[0]?.value
  });

  const {
    input: { value: level }
  } = useField('tournLateRegLevel', {
    subscription: { value: true },
    initialValue: tournLateRegLevelsOptions[0]?.value
  });

  const {
    input: { value: tournFormat }
  } = useField('tournFormat', { initialValue: tournFormatsOptions[0]?.value });
  const {
    input: { value: tournReentry }
  } = useField('tournReentry', { initialValue: tournReentryOptions[0]?.value });
  useField('tournSyncBreaks', { initialValue: tournSyncBreaksOptions[0]?.value });
  useField('payout', { initialValue: payoutsOptions[0]?.value });
  useSaveFormFields();

  const isRebuy = tournFormat.includes('REBUY');

  useEffect(() => {
    if (isRebuy && tournReentry === tournReentryOptions[1]?.value) {
      form.change('tournReentry', tournReentryOptions[0]?.value);
    }
  }, [form, isRebuy, tournReentry, tournReentryOptions]);

  useEffect(
    () => setState((previousState) => ({ ...previousState, activePage: ModalPages.MoreDetails })),
    [setState]
  );

  return (
    <Container>
      <div className={styles.moreDetails}>
        <div>
          <StarsDropDown
            id="levelLength"
            label={TXTCLI_Speed}
            options={tournLevelLengthsOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <div>
          <StarsDropDown
            id="tournFormat"
            label={TXTCLI_Format}
            options={tournFormatsOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <div>
          <StarsDropDown
            id="tournReentry"
            label={TXTCLI_Re_Entry}
            options={tournReentryOptions}
            className={styles.dropdown}
            disabled={loading || isRebuy}
          />
        </div>
        <div className={styles.lateWrapper}>
          <div className={styles.dropdownWrapper}>
            <StarsDropDown
              id="tournLateRegLevel"
              label={TXTCLI_Late_Registration}
              options={tournLateRegLevelsOptions}
              className={styles.dropdown}
              disabled={loading}
            />
          </div>
          <div className={styles.lateRegistration}>
            <span>{`${level * Speed[levelLength] || 0}`}</span>
            <span>
              <Translation {...TXTCLI_mins} />
            </span>
          </div>
        </div>
        <div>
          <StarsDropDown
            id="tournSyncBreaks"
            label={TXTCLI_Breaks}
            options={tournSyncBreaksOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <div className={styles.payout}>
          <StarsDropDown
            id="payout"
            label={TXTCLI_Payout}
            options={payoutsOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        {createClubTournamentResponse?.error && (
          <StarsInfo className={styles.errorPanel} type="error">
            {createClubTournamentResponse?.error}
          </StarsInfo>
        )}
      </div>
    </Container>
  );
};

export default MoreDetailsForm;
